import { faInfoCircle, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PriceFormat } from 'components/UI';
import AlertMessage from 'components/UI/V3/AlertMessage';
import Button from 'components/UI/V3/Button/Button';
import { FunctionComponent, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './PayoutModal.module.scss';

export interface PayoutModalProps {
    amount: number;
    amountFee: number;
    percentageFee: number;
    total: number;
    onClose: () => void;
    onConfirm: () => void | Promise<void>;
}

export const PayoutModal: FunctionComponent<PayoutModalProps> = props => {
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await props.onConfirm();
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className={styles.overlay}>
            <div
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="payout-modal-title"
            >
                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={props.onClose}
                    aria-label="Close"
                >
                    <FontAwesomeIcon
                        icon={faTimes}
                        fontSize={18}
                    />
                </button>

                <div className={styles.body}>
                    <h2
                        id="payout-modal-title"
                        className={styles.title}
                    >
                        Payout Instantly
                    </h2>
                    <p className={styles.description}>
                        Get today&apos;s pending funds in your bank account right away, instead of waiting for the
                        regular payout schedule.
                    </p>

                    <div className={styles.breakdown}>
                        <div className={styles.row}>
                            <span>Payout Amount</span>
                            <PriceFormat price={props.amount} />
                        </div>
                        <div className={styles.row}>
                            <span>Instant Payout Fee ({props.percentageFee}% + $0.5)</span>
                            <span>
                                - <PriceFormat price={props.amountFee} />
                            </span>
                        </div>
                        <hr className={styles.divider} />
                        <div className={`${styles.row} ${styles.totalRow}`}>
                            <span>You&apos;ll Receive</span>
                            <PriceFormat price={props.total} />
                        </div>
                    </div>

                    <AlertMessage
                        icon={faInfoCircle}
                        iconColor="#BAA997"
                        bdColor="#BCB8AE"
                        bgColor="#F8F5F1"
                        className={styles.alert}
                    >
                        <span className={styles.alertText}>
                            Funds typically arrive in your bank account within minutes.
                        </span>
                    </AlertMessage>
                </div>

                <div className={styles.footer}>
                    <Button
                        className={styles.button}
                        label="Cancel"
                        variant="black"
                        size="large"
                        fullWidth
                        onClick={props.onClose}
                        disabled={loading}
                    />
                    <Button
                        className={styles.button}
                        label="Yes, Confirm"
                        variant="green"
                        size="large"
                        fullWidth
                        onClick={handleConfirm}
                        isLoading={loading}
                        disabled={loading}
                    />
                </div>
            </div>
        </div>,
        document.body
    );
};

export default PayoutModal;
